import { ActionIcon, Group } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { translateNestingError } from "#app/i18n/translateApiError";
import { fontSizes } from "#app/theme/v1/fontSizes";
import { Alert } from "#shared/view/components/Alert";
import { Flex } from "#shared/view/components/Flex";
import { InlineLoader } from "#shared/view/components/InlineLoader";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { formatPercentage, JobStatusSchema, NestingRunLayoutView } from "shared";

import { useLayoutNesting } from "./useLayoutNesting";

type Props = {
  layoutId: string;
  errorMessage: string | null;
};

const IN_PROGRESS_STATUSES = new Set<string>([
  JobStatusSchema.enum.PENDING,
  JobStatusSchema.enum.STANDBY,
  JobStatusSchema.enum.PROCESSING,
]);

export const NestingRunViewer = ({ layoutId, errorMessage }: Props) => {
  const { t } = useTranslation();
  const { nestingRun } = useLayoutNesting(layoutId);
  const [position, setPosition] = useState(0);

  if (errorMessage) {
    return <Alert color="red">{errorMessage}</Alert>;
  }
  if (!nestingRun) {
    return null;
  }
  if (IN_PROGRESS_STATUSES.has(nestingRun.status)) {
    return (
      <Flex gap="xs" align="center">
        <InlineLoader size="sm" />
        <Text fz={fontSizes.small}>{t("layout.trueShapeNesting.loading")}</Text>
      </Flex>
    );
  }
  if (nestingRun.status === JobStatusSchema.enum.ERROR) {
    return (
      <Alert color="red">
        {[t("layout.trueShapeNesting.failed"), translateNestingError(nestingRun)].join(" : ")}
      </Alert>
    );
  }

  const { layouts } = nestingRun;
  const currentPosition = Math.min(position, layouts.length - 1);
  const currentLayout = layouts[currentPosition];
  if (!currentLayout) {
    return null;
  }

  return (
    <Stack gap="xs">
      {nestingRun.isFallback && (
        <Alert color="orange">{t("layout.trueShapeNesting.fallback")}</Alert>
      )}
      <LayoutHeader layout={currentLayout} />
      <img
        src={currentLayout.svgUrl}
        alt={t("layout.trueShapeNesting.svgAlt", { sheets: sheetNumbers(currentLayout) })}
        loading={currentPosition === 0 ? "eager" : "lazy"}
        style={{ width: "100%", height: "auto", display: "block" }}
      />
      {layouts.length > 1 && (
        <Group justify="center" gap="xs">
          <ActionIcon
            variant="subtle"
            disabled={currentPosition === 0}
            onClick={() => setPosition(currentPosition - 1)}
            aria-label={t("layout.trueShapeNesting.previous")}
          >
            <IconChevronLeft size={16} />
          </ActionIcon>
          <Text fz={fontSizes.small} w={80} ta="center">
            {currentPosition + 1} / {layouts.length}
          </Text>
          <ActionIcon
            variant="subtle"
            disabled={currentPosition === layouts.length - 1}
            onClick={() => setPosition(currentPosition + 1)}
            aria-label={t("layout.trueShapeNesting.next")}
          >
            <IconChevronRight size={16} />
          </ActionIcon>
        </Group>
      )}
    </Stack>
  );
};

const LayoutHeader = ({ layout }: { layout: NestingRunLayoutView }) => {
  const { t } = useTranslation();
  const sheetsLabelKey =
    layout.count > 1 ? "layout.trueShapeNesting.sheets" : "layout.trueShapeNesting.sheet";

  return (
    <Flex justify="space-between" align="baseline" gap="xs" wrap="wrap">
      <Text fz={fontSizes.small} fw={600}>
        {t(sheetsLabelKey, { sheets: sheetNumbers(layout) })}
        {layout.count > 1 && ` ${t("layout.trueShapeNesting.copies", { count: layout.count })}`}
      </Text>
      <Text fz={fontSizes.small} c="dimmed">
        {`${layout.widthMm} × ${layout.heightMm} mm · `}
        {t("layout.trueShapeNesting.pieces", { count: layout.pieceCount })}
        {` · ${t("layout.trueShapeNesting.utilization", {
          value: formatPercentage(layout.utilization * 100),
        })}`}
      </Text>
    </Flex>
  );
};

const sheetNumbers = (layout: NestingRunLayoutView) =>
  layout.sheetIndices.map((index) => index + 1).join(", ");
